import React, { useState, useEffect } from 'react';
import { Link, Plus, Trash2, Edit2, GripVertical, ExternalLink, Copy, Check, Instagram, Youtube, MessageCircle, Facebook, Twitter, Linkedin, Github, Globe, Share2, Eye } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const ICON_OPTIONS = [
  { value: 'link', label: 'Link', icon: Link },
  { value: 'instagram', label: 'Instagram', icon: Instagram },
  { value: 'youtube', label: 'YouTube', icon: Youtube },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { value: 'facebook', label: 'Facebook', icon: Facebook },
  { value: 'twitter', label: 'Twitter/X', icon: Twitter },
  { value: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { value: 'github', label: 'GitHub', icon: Github },
  { value: 'telegram', label: 'Telegram', icon: MessageCircle },
  { value: 'website', label: 'Website', icon: Globe },
];

// Auto-detect social media platform from URL
const detectPlatformFromUrl = (url) => {
  if (!url) return null;
  const lowerUrl = url.toLowerCase();
  
  // Instagram
  if (lowerUrl.includes('instagram.com') || lowerUrl.includes('instagr.am')) {
    return { icon: 'instagram', title: 'Instagram' };
  }
  // YouTube
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return { icon: 'youtube', title: 'YouTube' };
  }
  // WhatsApp
  if (lowerUrl.includes('wa.me') || lowerUrl.includes('whatsapp.com') || lowerUrl.includes('api.whatsapp')) {
    return { icon: 'whatsapp', title: 'WhatsApp' };
  }
  // Facebook
  if (lowerUrl.includes('facebook.com') || lowerUrl.includes('fb.com') || lowerUrl.includes('fb.me')) {
    return { icon: 'facebook', title: 'Facebook' };
  }
  // Twitter/X
  if (lowerUrl.includes('twitter.com') || lowerUrl.includes('x.com')) {
    return { icon: 'twitter', title: 'Twitter/X' };
  }
  // LinkedIn
  if (lowerUrl.includes('linkedin.com')) {
    return { icon: 'linkedin', title: 'LinkedIn' };
  }
  // GitHub
  if (lowerUrl.includes('github.com') || lowerUrl.includes('github.io')) {
    return { icon: 'github', title: 'GitHub' };
  }
  // Telegram
  if (lowerUrl.includes('t.me') || lowerUrl.includes('telegram.me') || lowerUrl.includes('telegram.org')) {
    return { icon: 'telegram', title: 'Telegram' };
  }
  // TikTok
  if (lowerUrl.includes('tiktok.com')) {
    return { icon: 'link', title: 'TikTok' };
  }
  // Snapchat
  if (lowerUrl.includes('snapchat.com')) {
    return { icon: 'link', title: 'Snapchat' };
  }
  // Pinterest
  if (lowerUrl.includes('pinterest.com') || lowerUrl.includes('pin.it')) {
    return { icon: 'link', title: 'Pinterest' };
  }
  // Spotify
  if (lowerUrl.includes('spotify.com') || lowerUrl.includes('open.spotify')) {
    return { icon: 'link', title: 'Spotify' };
  }
  // Discord
  if (lowerUrl.includes('discord.gg') || lowerUrl.includes('discord.com')) {
    return { icon: 'link', title: 'Discord' };
  }
  // Threads
  if (lowerUrl.includes('threads.net')) {
    return { icon: 'link', title: 'Threads' };
  }
  
  return null;
};

const LinkInBio = () => {
  const { user } = useAuth();
  const [linkbio, setLinkbio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [formData, setFormData] = useState({ title: '', url: '', icon: 'link' });
  const [bio, setBio] = useState('');
  const [themeColor, setThemeColor] = useState('#8B5CF6');
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  const publicUrl = `${window.location.origin}/link/${user?.username}`;

  useEffect(() => {
    fetchLinkBio();
  }, []);

  const fetchLinkBio = async () => {
    try {
      const response = await api.get('/linkbio/my-links');
      setLinkbio(response.data);
      setBio(response.data.bio || '');
      setThemeColor(response.data.theme_color || '#8B5CF6');
    } catch (error) {
      console.error('Failed to fetch link bio:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle URL change with auto-detect
  const handleUrlChange = (url) => {
    const detected = detectPlatformFromUrl(url);
    
    if (detected) {
      // Auto-fill icon and suggest title if empty
      setFormData(prev => ({
        ...prev,
        url: url,
        icon: detected.icon,
        title: prev.title || detected.title
      }));
      toast.success(`Detected ${detected.title}!`, { duration: 1500, icon: '✨' });
    } else {
      setFormData(prev => ({ ...prev, url: url }));
    }
  };

  const handleAddLink = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.url.trim()) {
      toast.error('Please fill all fields');
      return;
    }

    // Auto-format URL
    let url = formData.url.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url;
    }

    setSaving(true);
    try {
      if (editingLink) {
        await api.put(`/linkbio/update-link/${editingLink.id}`, {
          title: formData.title,
          url: url,
          icon: formData.icon
        });
        toast.success('Link updated!');
      } else {
        await api.post('/linkbio/add-link', {
          title: formData.title,
          url: url,
          icon: formData.icon
        });
        toast.success('Link added!');
      }
      setShowAddModal(false);
      setEditingLink(null);
      setFormData({ title: '', url: '', icon: 'link' });
      fetchLinkBio();
    } catch (error) {
      toast.error('Failed to save link');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Delete this link?')) return;
    
    try {
      await api.delete(`/linkbio/delete-link/${linkId}`);
      toast.success('Link deleted');
      fetchLinkBio();
    } catch (error) {
      toast.error('Failed to delete link');
    }
  };

  const handleUpdateBio = async () => {
    setSaving(true);
    try {
      await api.put('/linkbio/update-bio', {
        bio: bio,
        theme_color: themeColor
      });
      toast.success('Bio updated!');
    } catch (error) {
      toast.error('Failed to update bio');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const openEdit = (link) => {
    setEditingLink(link);
    setFormData({
      title: link.title,
      url: link.url,
      icon: link.icon || 'link'
    });
    setShowAddModal(true);
  };

  const getIconComponent = (iconName) => {
    const found = ICON_OPTIONS.find(i => i.value === iconName);
    return found ? found.icon : Link;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full mb-4">
            <Link className="w-4 h-4 text-green-500" />
            <span className="text-green-500 font-medium">FREE Feature</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">Link in Bio</h1>
          <p className="text-muted-foreground">One link to share all your social profiles</p>
        </div>

        {/* Public URL Card */}
        <div className="glass-card p-6 mb-8 border-2 border-primary/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Your Public Link</p>
              <p className="text-lg font-mono text-primary break-all">{publicUrl}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={copyLink}
                className="flex items-center gap-2 px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-secondary/20 text-secondary rounded-lg hover:bg-secondary/30"
              >
                <Eye className="w-4 h-4" />
                Preview
              </a>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Bio & Settings */}
          <div className="glass-card p-6">
            <h2 className="text-xl font-bold mb-4">Profile Settings</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Bio / Description</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  placeholder="Write a short bio..."
                  rows={3}
                  maxLength={200}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary resize-none"
                />
                <p className="text-xs text-muted-foreground mt-1">{bio.length}/200</p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Theme Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={themeColor}
                    onChange={(e) => setThemeColor(e.target.value)}
                    className="w-12 h-12 rounded-lg cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={themeColor}
                    onChange={(e) => setThemeColor(e.target.value)}
                    className="flex-1 px-4 py-3 bg-white/5 border border-white/10 rounded-xl font-mono"
                  />
                </div>
              </div>

              <button
                onClick={handleUpdateBio}
                disabled={saving}
                className="w-full btn-primary"
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>

            {/* Stats */}
            <div className="mt-6 pt-6 border-t border-white/10">
              <h3 className="font-semibold mb-3">Stats</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-primary">{linkbio?.links?.length || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Links</p>
                </div>
                <div className="bg-white/5 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-500">{linkbio?.total_clicks || 0}</p>
                  <p className="text-xs text-muted-foreground">Total Clicks</p>
                </div>
              </div>
            </div>
          </div>

          {/* Links Manager */}
          <div className="glass-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">My Links</h2>
              <button
                onClick={() => {
                  setEditingLink(null);
                  setFormData({ title: '', url: '', icon: 'link' });
                  setShowAddModal(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-500 rounded-lg hover:bg-green-500/30"
              >
                <Plus className="w-4 h-4" />
                Add Link
              </button>
            </div>

            {linkbio?.links?.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Link className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No links yet. Add your first link!</p>
              </div>
            ) : (
              <div className="space-y-3">
                {linkbio?.links?.map((link) => {
                  const IconComp = getIconComponent(link.icon);
                  return (
                    <div
                      key={link.id}
                      className="flex items-center gap-3 p-4 bg-white/5 rounded-xl group hover:bg-white/10 transition-colors"
                    >
                      <div className="p-2 rounded-lg" style={{ backgroundColor: themeColor + '30' }}>
                        <IconComp className="w-5 h-5" style={{ color: themeColor }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{link.title}</p>
                        <p className="text-xs text-muted-foreground truncate">{link.url}</p>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="text-xs text-muted-foreground mr-2">{link.clicks || 0} clicks</span>
                        <button
                          onClick={() => openEdit(link)}
                          className="p-2 hover:bg-white/10 rounded-lg"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteLink(link.id)}
                          className="p-2 hover:bg-red-500/20 rounded-lg text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Add/Edit Link Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-6 max-w-md w-full">
              <h2 className="text-xl font-bold mb-4">
                {editingLink ? 'Edit Link' : 'Add New Link'}
              </h2>
              
              <form onSubmit={handleAddLink} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Link Title</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Follow me on Instagram"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">URL</label>
                  <input
                    type="text"
                    value={formData.url}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder="Paste your link - we'll auto-detect the platform!"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:border-primary"
                    required
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Tip: Paste a URL and we'll auto-detect Instagram, YouTube, WhatsApp, etc.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Icon</label>
                  <div className="grid grid-cols-5 gap-2">
                    {ICON_OPTIONS.map((opt) => {
                      const Icon = opt.icon;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, icon: opt.value })}
                          className={`p-3 rounded-xl flex flex-col items-center gap-1 transition-colors ${
                            formData.icon === opt.value
                              ? 'bg-primary/30 border-2 border-primary'
                              : 'bg-white/5 border border-white/10 hover:bg-white/10'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="text-[10px]">{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 btn-primary"
                  >
                    {saving ? 'Saving...' : editingLink ? 'Update' : 'Add Link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingLink(null);
                    }}
                    className="px-6 py-3 bg-white/10 rounded-xl hover:bg-white/20"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LinkInBio;
